/** One combined complimentary case-and-cloth line for all eyewear in the cart. */
(() => {
  const COMPLIMENTARY_SET_ID = 64251867332985;
  const LEGACY_CLOTH_ID = 64251870937465;
  const GIFT_IDS = new Set([COMPLIMENTARY_SET_ID, LEGACY_CLOTH_ID]);
  const nativeFetch = window.fetch.bind(window);
  let syncQueue = Promise.resolve();
  let reloading = false;

  const idOf = (item) => Number(item.variant_id || item.id);
  const isGift = (item) => GIFT_IDS.has(idOf(item));
  const isCanonicalGift = (item) => (
    idOf(item) === COMPLIMENTARY_SET_ID
    && item.properties?._complimentary_accessory === 'true'
    && !item.properties?._og_parent_key
    && !item.properties?._og_parent_unit
  );

  async function cartJson() {
    const response = await nativeFetch(`${window.Shopify.routes.root}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Unable to read cart.');
    return response.json();
  }

  function totalEyewearQuantity(cart) {
    // Normal sellable products in this store are eyeglasses or sunglasses.
    return cart.items
      .filter((item) => !isGift(item))
      .reduce((total, item) => total + Number(item.quantity || 0), 0);
  }

  async function reconcile(cart) {
    const wantedQuantity = totalEyewearQuantity(cart);
    const gifts = cart.items.filter(isGift);
    const canonical = gifts.find(isCanonicalGift);
    const updates = {};

    gifts.forEach((item) => {
      if (item !== canonical) updates[item.key] = 0;
    });
    if (canonical && canonical.quantity !== wantedQuantity) {
      updates[canonical.key] = wantedQuantity;
    }

    if (Object.keys(updates).length) {
      const response = await nativeFetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) throw new Error('Unable to update the complimentary item.');
    }

    if (wantedQuantity > 0 && !canonical) {
      const response = await nativeFetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{
            id: COMPLIMENTARY_SET_ID,
            quantity: wantedQuantity,
            properties: { _complimentary_accessory: 'true' },
          }],
        }),
      });
      if (!response.ok) throw new Error('Unable to add the complimentary item.');
    }

    return Object.keys(updates).length > 0 || (wantedQuantity > 0 && !canonical);
  }

  function lockGiftQuantity() {
    document.querySelectorAll('tr[data-line-key], li[data-line-key]').forEach((line) => {
      const gift = GIFT_IDS.has(Number(line.dataset.variantId));
      line.classList.toggle('complimentary-cart-item', gift);
      if (!gift) return;

      // Shopify may place the complimentary line first because it was updated
      // most recently. Keep the eyewear first and the single free set last.
      if (line.parentElement?.lastElementChild !== line) {
        line.parentElement.appendChild(line);
      }

      line.querySelectorAll('quantity-input button, cart-remove-button, [name="updates[]"]').forEach((control) => {
        control.setAttribute('disabled', 'disabled');
        control.setAttribute('aria-disabled', 'true');
      });
    });
  }

  function updateVisibleGiftQuantity(cart) {
    const giftItem = cart.items.find((item) => idOf(item) === COMPLIMENTARY_SET_ID);
    const quantity = giftItem ? Number(giftItem.quantity) : 0;

    document.querySelectorAll('tr[data-line-key], li[data-line-key]').forEach((line) => {
      if (!GIFT_IDS.has(Number(line.dataset.variantId))) return;
      if (quantity === 0) {
        line.remove();
        return;
      }
      line.dataset.quantity = String(quantity);
      line.querySelectorAll('[name="updates[]"], .quantity__input').forEach((input) => {
        input.value = String(quantity);
        input.setAttribute('value', String(quantity));
      });
    });
    lockGiftQuantity();
  }

  async function refreshCartHtml() {
    const response = await nativeFetch(`${window.Shopify.routes.root}cart?og_refresh=${Date.now()}`, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      cache: 'no-store',
    });
    if (!response.ok) return;
    const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
    [
      'cart-items.page-width',
      '#main-cart-footer',
      '#mini-cart',
      '#cart-icon-bubble',
      '#mobile-cart-icon-bubble',
    ].forEach((selector) => {
      const current = document.querySelector(selector);
      const fresh = parsed.querySelector(selector);
      if (current && fresh) current.replaceWith(fresh);
    });
    lockGiftQuantity();
  }

  async function performSync() {
    if (reloading) return;
    try {
      const cartBeforeSync = await cartJson();
      const changed = await reconcile(cartBeforeSync);
      if (changed && /(^|\/)cart\/?$/.test(window.location.pathname)) {
        reloading = true;
        window.location.reload();
        return;
      }
      if (changed) {
        const finalCart = await cartJson();
        updateVisibleGiftQuantity(finalCart);
        await refreshCartHtml();
        updateVisibleGiftQuantity(finalCart);
      } else {
        updateVisibleGiftQuantity(cartBeforeSync);
      }
    } catch (error) {
      console.error('[Complimentary accessory]', error);
    }
  }

  function sync() {
    syncQueue = syncQueue.then(performSync, performSync);
    return syncQueue;
  }

  window.OGComplimentaryAccessories = { sync };
  document.addEventListener('DOMContentLoaded', () => {
    if (/(^|\/)cart\/?$/.test(window.location.pathname)) sync();
  });
})();
